/**
 * Relatórios: previsto × real, pontualidade e paradas.
 *
 * É o que o sistema entrega e o papel não entregava. O orçamento da gráfica já
 * diz que envelope leva 9:40; até agora ninguém sabia se leva. Aqui o desvio
 * aparece por etapa, por máquina, por pessoa e por tipo de produto — e vira
 * conversa com quem orça.
 *
 * **O relatório por pessoa é dado sensível** (§12.5 do PRD): mede o trabalho de
 * alguém. Ele existe para gestão de produção, não para vigilância, e a tela diz
 * isso em vez de fingir que é só mais uma aba.
 */

import { formato, type EventoProducao } from '@pcpgbu/dominio';
import { botao, cabecalhoDePagina, h, render, sf, tituloDeSecao, vazio } from '@pcpgbu/ui';
import type { Aplicacao } from '../estado.js';

type Eixo = 'etapa' | 'estacao' | 'operador' | 'produto';

const EIXOS: Array<{ id: Eixo; rotulo: string }> = [
  { id: 'etapa', rotulo: 'Por etapa' },
  { id: 'estacao', rotulo: 'Por máquina' },
  { id: 'produto', rotulo: 'Por tipo de produto' },
  { id: 'operador', rotulo: 'Por pessoa' },
];

interface Agregado {
  chave: string;
  execucoes: number;
  previstoSeg: number;
  realSeg: number;
  qtdBoa: number;
  qtdRefugo: number;
}

let eixoAtual: Eixo = 'etapa';

export async function telaRelatorios(app: Aplicacao, destino: HTMLElement): Promise<void> {
  const carteira = await app.repo.carteira();
  const cadastros = await app.repo.carregarCadastros();
  const eventos = await app.repo.todosOsEventos();

  // Reúne todas as etapas concluídas com tempo medido.
  const detalhes = await Promise.all(carteira.map((l) => app.repo.detalhe(l.os.numero)));
  const concluidas = detalhes
    .filter((d) => d !== null)
    .flatMap((d) =>
      d!.etapas
        .filter((e) => e.etapa.tipoLinha === 'operacao' && e.estado.status === 'concluida')
        .map((e) => ({ os: d!.os, ...e })),
    );

  const desenhar = (): void => {
    render(destino, [
      h('.vstack.gap-4', [
      cabecalhoDePagina({
        olho: 'Análise',
        titulo: 'Relatórios',
        lead: 'Previsto contra real, paradas e pontualidade. Os números são dos apontamentos, não de estimativa.',
      }),

        blocoPontualidade(carteira),
        h('.cartao', [
          h('.hstack.gap-3.wrap', [
            h('.bloco-t', { style: { padding: 0 } }, 'Previsto × real'),
            h('.spacer'),
            h(
              '.mac-segmented',
              EIXOS.map((e) =>
                h(
                  `button${e.id === eixoAtual ? '.is-active' : ''}`,
                  {
                    type: 'button',
                    onclick: () => {
                      eixoAtual = e.id;
                      desenhar();
                    },
                  },
                  e.rotulo,
                ),
              ),
            ),
            botao('Exportar CSV', () => exportar(agregar(), eixoAtual), { icone: 'square-and-arrow-down', pequeno: true }),
          ]),
          eixoAtual === 'operador'
            ? h('.t-subhead.t-secondary', { style: { paddingBottom: 'var(--sp-2)' } }, [
                sf('lock', 'sf-xs'),
                ' Tempo por pessoa é dado de trabalho. Serve para planejar a produção — não para ranking, e só a direção e o PCP veem.',
              ])
            : null,
          concluidas.length === 0
            ? vazio(
                'sum',
                'Nada medido ainda',
                'Assim que as etapas começarem a ser apontadas no tablet, o tempo real aparece aqui ao lado do previsto na OS.',
              )
            : tabela(agregar()),
        ]),
        blocoPausas(eventos, cadastros),
      ]),
    ]);
  };

  function agregar(): Agregado[] {
    const mapa = new Map<string, Agregado>();
    const nomeEstacao = new Map(cadastros.estacoes.map((e) => [e.id, e.nome]));
    const nomePessoa = new Map(cadastros.pessoas.map((p) => [p.id, p.nome]));

    for (const c of concluidas) {
      const chaves =
        eixoAtual === 'etapa'
          ? [c.etapa.nomeOs]
          : eixoAtual === 'estacao'
            ? [c.estacao?.nome ?? 'sem estação']
            : eixoAtual === 'produto'
              ? [(/^([^-]+)/.exec(c.os.produto)?.[1] ?? c.os.produto).trim()]
              : c.estado.operadores.length > 0
                ? c.estado.operadores.map((id) => nomePessoa.get(id) ?? '—')
                : ['—'];

      for (const chave of chaves) {
        const a = mapa.get(chave) ?? {
          chave,
          execucoes: 0,
          previstoSeg: 0,
          realSeg: 0,
          qtdBoa: 0,
          qtdRefugo: 0,
        };
        a.execucoes += 1;
        a.previstoSeg += c.etapa.tempoPrevistoSeg ?? 0;
        a.realSeg += c.estado.tempoRealSeg;
        a.qtdBoa += c.estado.qtdBoa ?? 0;
        a.qtdRefugo += c.estado.qtdRefugo ?? 0;
        mapa.set(chave, a);
      }
    }
    void nomeEstacao;
    return [...mapa.values()].sort((x, y) => y.realSeg - x.realSeg);
  }

  desenhar();
}

/* ------------------------------------------------------------------ */

function tabela(dados: Agregado[]): HTMLElement {
  const totalPrev = dados.reduce((s, d) => s + d.previstoSeg, 0);
  const totalReal = dados.reduce((s, d) => s + d.realSeg, 0);

  return h('table.tab', [
    h('thead', h('tr', [
      h('th', 'Nome'),
      h('th', { style: { width: '80px', textAlign: 'right' } }, 'Vezes'),
      h('th', { style: { width: '96px', textAlign: 'right' } }, 'Previsto'),
      h('th', { style: { width: '96px', textAlign: 'right' } }, 'Real'),
      h('th', { style: { width: '96px', textAlign: 'right' } }, 'Desvio'),
      h('th', { style: { width: '130px' } }, ''),
      h('th', { style: { width: '110px', textAlign: 'right' } }, 'Refugo'),
    ])),
    h('tbody', [
      ...dados.map((d) => {
        const pct = d.previstoSeg > 0 ? ((d.realSeg - d.previstoSeg) / d.previstoSeg) * 100 : null;
        const cor = pct === null ? 'var(--label-3)' : pct > 10 ? 'var(--sys-red)' : pct < -10 ? 'var(--sys-green)' : 'var(--label-2)';
        const largura = pct === null ? 0 : Math.min(100, Math.abs(pct));
        return h('tr', [
          h('td', d.chave),
          h('td.num', formato.numero(d.execucoes)),
          h('td.num', formato.hhmm(d.previstoSeg)),
          h('td.num', { style: { fontWeight: '600' } }, formato.hhmm(d.realSeg)),
          h('td.num', { style: { color: cor, fontWeight: '600' } }, pct === null ? '—' : formato.desvio(pct)),
          h('td', h('.pcp-carga-barra', { style: { '--pcp-cor': cor } },
            h('i', { style: { width: `${largura}%` } }))),
          h('td.num.t-secondary',
            d.qtdBoa + d.qtdRefugo > 0
              ? `${formato.numero((d.qtdRefugo / (d.qtdBoa + d.qtdRefugo)) * 100, 1)}%`
              : '—'),
        ]);
      }),
      h('tr', { style: { fontWeight: '600', background: 'var(--fill-4)' } }, [
        h('td', 'Total'),
        h('td.num', formato.numero(dados.reduce((s, d) => s + d.execucoes, 0))),
        h('td.num', formato.hhmm(totalPrev)),
        h('td.num', formato.hhmm(totalReal)),
        h('td.num', totalPrev > 0 ? formato.desvio(((totalReal - totalPrev) / totalPrev) * 100) : '—'),
        h('td'),
        h('td'),
      ]),
    ]),
  ]);
}

function blocoPontualidade(carteira: Awaited<ReturnType<Aplicacao['repo']['carteira']>>): HTMLElement {
  const fechadas = carteira.filter(
    (l) => l.estado.status === 'pronta' || l.estado.status === 'concluida',
  );
  if (fechadas.length === 0) {
    return h('.cartao', [
      h('.bloco-t', 'Pontualidade'),
      h('.t-callout.t-secondary', 'Aparece quando as primeiras OS forem concluídas no sistema.'),
    ]);
  }

  const noPrazo = fechadas.filter((l) => {
    const fim = l.estado.prontaEm ?? l.os.prontaEm ?? l.os.concluidaEm;
    return fim ? Date.parse(fim) <= Date.parse(l.os.previsaoEntregaEm) : false;
  });
  const pct = (noPrazo.length / fechadas.length) * 100;

  return h('.cartao', [
    h('.bloco-t', 'Pontualidade'),
    h('.hstack.gap-6.wrap', [
      h('.vstack', { style: { gap: '0' } }, [
        h('span', { style: { font: 'var(--fw-bold) 40px/44px var(--font-display)', color: pct >= 90 ? 'var(--sys-green)' : pct >= 70 ? 'var(--sys-orange)' : 'var(--sys-red)' } },
          `${formato.numero(pct, 0)}%`),
        h('.t-subhead.t-secondary', 'entregues no prazo'),
      ]),
      h('.vstack', { style: { gap: '0' } }, [
        h('span.t-title1.pcp-num', String(fechadas.length)),
        h('.t-subhead.t-secondary', 'OS terminadas'),
      ]),
      h('.vstack', { style: { gap: '0' } }, [
        h('span.t-title1.pcp-num', String(fechadas.length - noPrazo.length)),
        h('.t-subhead.t-secondary', 'fora do prazo'),
      ]),
    ]),
  ]);
}

function blocoPausas(
  eventos: EventoProducao[],
  cadastros: Awaited<ReturnType<Aplicacao['repo']['carregarCadastros']>>,
): HTMLElement {
  const pausas = eventos.filter((e) => e.tipo === 'pausa');
  if (pausas.length === 0) {
    return h('.cartao', [
      h('.bloco-t', 'Paradas'),
      h('.t-callout.t-secondary', 'Nenhuma pausa registrada ainda.'),
    ]);
  }

  const porMotivo = new Map<string, number>();
  for (const p of pausas) {
    const nome = cadastros.motivos.find((m) => m.id === p.motivoId)?.descricao ?? 'Sem motivo';
    porMotivo.set(nome, (porMotivo.get(nome) ?? 0) + 1);
  }
  const linhas = [...porMotivo.entries()].sort((a, b) => b[1] - a[1]);
  const maior = Math.max(...linhas.map(([, n]) => n));

  return h('.cartao', [
    tituloDeSecao(`Paradas · ${pausas.length}`),
    h('.pcp-carga',
      linhas.map(([nome, n]) =>
        h('.pcp-carga-linha', { style: { '--pcp-cor': 'var(--sys-orange)' } }, [
          h('span.t-callout', nome),
          h('.pcp-carga-barra', h('i', { style: { width: `${(n / maior) * 100}%` } })),
          h('.pcp-carga-valor', String(n)),
        ]),
      ),
    ),
  ]);
}

function exportar(dados: Agregado[], eixo: Eixo): void {
  const cabecalho = ['Nome', 'Vezes', 'Previsto (h:mm)', 'Real (h:mm)', 'Desvio %', 'Boas', 'Refugo'];
  const linhas = dados.map((d) => {
    const pct = d.previstoSeg > 0 ? ((d.realSeg - d.previstoSeg) / d.previstoSeg) * 100 : null;
    return [
      d.chave,
      d.execucoes,
      formato.hhmm(d.previstoSeg),
      formato.hhmm(d.realSeg),
      pct === null ? '' : pct.toFixed(1).replace('.', ','),
      d.qtdBoa,
      d.qtdRefugo,
    ];
  });

  // Ponto e vírgula e BOM: é assim que o Excel em português abre sem pedir nada.
  const csv = [cabecalho, ...linhas]
    .map((l) => l.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(';'))
    .join('\r\n');
  // O BOM (\uFEFF) na frente não é sujeira: sem ele o Excel em português
  // abre o CSV como ANSI e todo acento vira símbolo. Escrito como escape
  // porque o caractere literal é invisível e some ao copiar e colar.
  const blob = new Blob([`\uFEFF${csv}`], { type: 'text/csv;charset=utf-8' });

  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `pcpgbu-previsto-real-${eixo}-${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 4000);
}
